/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Container from 'react-bootstrap/Container'
import * as data from '../data'
import { StatusContext, StatusContextType } from './StatusContext'
import { JsonEditor, githubDarkTheme } from 'json-edit-react'

function CategoryInfo(props: { catKey: any }) {
    const key = props.catKey;
    const [ formdata, setFormdata ] = useState<data.Category>();
    const [ originalFormdata, setOriginalFormdata ] = useState<data.Category>();
    const [ supercats, setSupercats ] = useState<data.Supercat[]>([]);
    const statusContext = useContext(StatusContext);

    useEffect(() => {
      async function fetchCategory() {
        try {
            const response = await data.getCategory(key);
            setFormdata(response);
            setOriginalFormdata(JSON.parse(JSON.stringify(response)));
        } catch (error:any) {
            statusContext.postError(`Error fetching category ${key} : ${error.message}`);
            console.error('Error fetching category ${key}:', error);
        }
      }
      fetchCategory();
    }, [ key ]);

    useEffect(() => {
      async function fetchSupercats() {
        try {
            const response = await data.getSupercat();
            setSupercats(response || []);
        } catch (error:any) {
            statusContext.postError(`Error fetching supercategories: ${error.message}`);
            console.error('Error fetching supercategories:', error);
        }
      }
      fetchSupercats();
    }, []);

    const titleClass = formdata?.title !== originalFormdata?.title ? "bg-warning" : "";
    const descriptionClass = formdata?.description !== originalFormdata?.description ? "bg-warning" : "";
    const themeColorClass = formdata?.presentation?.themeColor !== originalFormdata?.presentation?.themeColor ? "bg-warning" : "";
    const themeHighlightColorClass = formdata?.presentation?.themeHighlightColor !== originalFormdata?.presentation?.themeHighlightColor ? "bg-warning" : "";
    const supercatClass = JSON.stringify(formdata?.presentation?.supercat) !== JSON.stringify(originalFormdata?.presentation?.supercat) ? "bg-warning" : "";
    const presentationClass = JSON.stringify(formdata?.presentation) !== JSON.stringify(originalFormdata?.presentation) ? "bg-warning" : "";
    const clean = titleClass === "" && descriptionClass === "" && themeColorClass === "" && themeHighlightColorClass === "" && supercatClass === "";

    const supercatArray = Array.isArray(formdata?.presentation?.supercat) ? formdata.presentation.supercat :
                          typeof formdata?.presentation?.supercat === 'string' ? [formdata.presentation.supercat] : [];
    const supercatDisabled = supercatArray.length > 1;

    return (
        <div className="container-fluid">
            <h3 className="pt-2">Category ID: <code>{key}</code></h3>
            <form>
                <div className="form-group">
                    <label htmlFor="inputTitle">Title</label>
                    <input type="string" className={"form-control " + titleClass} id="inputTitle" aria-describedby="titleHelp"
                        value={formdata?.title ?? ''}
                        onChange={e => {
                            let newValue = String(e.target.value);
                            setFormdata({ ...formdata, title: newValue } as data.Category);
                        }}
                        placeholder="Enter Title" />
                    <small id="titleHelp" className="form-text text-muted">Short title for the category.</small>
                </div>
                <div className="form-group pt-2 pb-3">
                    <label htmlFor="inputDescription">Description</label>
                    <textarea
                        rows={3}
                        className={"form-control " + descriptionClass}
                        id="inputDescription"
                        placeholder="Description"
                        aria-describedby="descriptionHelp"
                        value={formdata?.description ?? ''}
                        onChange={e => {
                            let newValue = String(e.target.value);
                            setFormdata({ ...formdata, description: newValue } as data.Category);
                        }}
                    />
                    <small id="descriptionHelp" className="form-text text-muted">Full description for category.</small>
                </div>
                <div className="row border-top pt-3 pb-3">
                    <div className="form-group col-md-6">
                        <label htmlFor="inputThemeColor">Color Override</label>
                        <input
                            type="text"
                            className={"form-control " + themeColorClass}
                            id="inputThemeColor"
                            placeholder="Theme Color"
                            aria-describedby="themeColorHelp"
                            value={formdata?.presentation?.themeColor as string || ""}
                            onChange={e => {
                                let newValue = e.target.value;
                                let newfd = Object.assign({}, formdata);
                                if (newfd.presentation) {
                                    newfd.presentation.themeColor = newValue;
                                } else {
                                    newfd.presentation = { themeColor: newValue };
                                }
                                setFormdata(newfd);
                            }}
                        />
                        <small id="themeColorHelp" className="form-text text-muted">Primary theme color for the category.</small>
                    </div>
                    <div className="form-group col-md-6">
                        <label htmlFor="inputThemeHighlightColor">Highlight Color Override</label>
                        <input
                            type="text"
                            className={"form-control " + themeHighlightColorClass}
                            id="inputThemeHighlightColor"
                            placeholder="Theme Highlight Color"
                            aria-describedby="themeHighlightColorHelp"
                            value={formdata?.presentation?.themeHighlightColor as string || ""}
                            onChange={e => {
                                let newValue = e.target.value;
                                let newfd = Object.assign({}, formdata);
                                if (newfd.presentation) {
                                    newfd.presentation.themeHighlightColor = newValue;
                                } else {
                                    newfd.presentation = { themeHighlightColor: newValue };
                                }
                                setFormdata(newfd);
                            }}
                        />
                        <small id="themeHighlightColorHelp" className="form-text text-muted">Highlight theme color for the category.</small>
                    </div>
                </div>
                <div className="row pt-3 pb-3">
                    <div className="form-group col-md-12">
                        <label htmlFor="inputSupercat">Supercategory</label>
                        <select
                            className={"form-control " + supercatClass}
                            id="inputSupercat"
                            disabled={supercatDisabled}
                            value={supercatArray.length > 0 ? supercatArray[0] : ""}
                            onChange={e => {
                                let newValue = e.target.value;
                                let newfd = Object.assign({}, formdata);
                                if (newfd.presentation) {
                                    newfd.presentation.supercat = newValue ? [newValue] : [];
                                } else {
                                    newfd.presentation = { supercat: newValue ? [newValue] : [] };
                                }
                                setFormdata(newfd);
                            }}
                            aria-describedby="supercatHelp"
                        >
                            <option value="">-- Select Supercategory --</option>
                            {supercats.filter((sc) => sc.key !== 'ALL').map((sc) => (
                                <option key={sc.key} value={sc.key}>{sc.title}</option>
                            ))}
                        </select>
                        <small id="supercatHelp" className="form-text text-muted">
                            {supercatDisabled ? "Disabled: Multiple supercategories assigned" : "Primary supercategory for the category."}
                        </small>
                    </div>
                </div>
                <div className="row pt-3 pb-3 border-top">
                    <h5>Advanced: Presentation (JSON)</h5>
                    <div style={{
                        backgroundColor: '#1e1e1e',
                        borderRadius: '4px',
                        padding: '15px',
                        border: presentationClass ? '2px solid #ffc107' : '1px solid #ccc',
                        marginBottom: '15px'
                    }}>
                        <JsonEditor
                            data={formdata?.presentation || {}}
                            collapse={2}
                            rootName="presentation"
                            rootFontSize="13px"
                            theme={githubDarkTheme}
                            onUpdate={({ newData }) => {
                                setFormdata({ ...formdata, presentation: newData } as data.Category);
                            }}
                        />
                    </div>
                </div>
                <div className="row pt-3 justify-content-end border-top">
                    <div className="col-2">
                        <button type="button"
                                className="btn btn-primary float-end"
                                disabled={clean}
                                onClick={async (e) => {
                                    if (!formdata) {
                                        return;
                                    }
                                    try {
                                        await data.setCategory(formdata);
                                        setOriginalFormdata(JSON.parse(JSON.stringify(formdata)));
                                    } catch (error:any) {
                                        statusContext.postError(`Error saving category ${key} : ${error.message}`);
                                        console.error('Error saving category ${key}:', error);
                                    }
                                    e.stopPropagation();
                                }}
                        >
                            <span>Save</span>
                        </button>
                    </div>
                </div>
            </form>
        </div >
  )
}

export default CategoryInfo;